/**
 * <StudentPasswordPage> — « Mot de passe » (page).
 *
 * Trois champs masqués, chacun avec son œil : l'ancien mot de passe, le nouveau, sa confirmation.
 * Les erreurs de champ (« Champ obligatoire », huit caractères minimum, confirmation différente)
 * arrivent en props, comme l'erreur du serveur que la coque pose au-dessus du bouton. Rien à
 * charger : la page n'a pas de squelette.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { TextInput } from "../../atoms/TextInput"
import { Card } from "../../molecules/Card"
import { AccountSettingsTemplate, type AccountSettingsShellProps } from "../../templates/AccountSettingsTemplate"
import { type UIPasswordChange } from "../../../ports/primary/ui/types"

export type { UIPasswordChange }

export type PasswordChangeField = keyof UIPasswordChange

export type StudentPasswordPageProps = Omit<AccountSettingsShellProps, "loading" | "error" | "onRetry"> & {
    values: UIPasswordChange
    errors?: Partial<Record<PasswordChangeField, string>>
    onChange: (field: PasswordChangeField, value: string) => void
}

export function StudentPasswordPage(props: StudentPasswordPageProps) {
    const { values, errors = {}, onChange, ...shell } = props

    const field = (name: PasswordChangeField) => ({
        value: values[name],
        onChangeText: (text: string) => onChange(name, text),
        error: errors[name],
        disabled: shell.submitting === true,
        secureTextEntry: true,
        autoCapitalize: "none" as const,
    })

    return (
        <AccountSettingsTemplate {...shell} title="Mot de passe">
            <Card p="4">
                <Stack gap="3">
                    <TextInput {...field("currentPassword")} label="Ancien mot de passe" textContentType="password" />
                    <TextInput {...field("password")} label="Nouveau mot de passe" textContentType="newPassword" />
                    <TextInput
                        {...field("passwordConfirmation")}
                        label="Confirmer le mot de passe"
                        textContentType="newPassword"
                    />
                </Stack>
            </Card>
        </AccountSettingsTemplate>
    )
}
